import process from 'process';

import { GetStaticPaths, GetStaticProps } from 'next';
import {
  DrawDetailItemFragment,
  NavigationItemFragment,
  PageDetailFragment,
} from '@amf/shared/types/graphql';
import { sdk } from '@amf/shared/lib/graphQlClient';
import {
  getNavigation,
  getPartners,
  NavigationProps,
  NavigationType,
} from '@amf/shared/utils/navigation';
import { getContentDescription, getContentImageUrl } from '@amf/shared/utils/content';
import Content from '@amf/shared/components/content/Content';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import { useTranslate } from '@tolgee/react';

import Layout from 'components/layout/Layout';
import Meta from 'components/Meta';
import Container from 'components/layout/Container';
import HomeGroups from 'components/home/HomeGroups';
import FanRegister from 'components/utils/FanRegister';

interface StaticPageProps extends NavigationProps {
  page: PageDetailFragment;
  draw: DrawDetailItemFragment | null;
}

export default function StaticPage({ page, navigation, partners, draw }: StaticPageProps) {
  const { t } = useTranslate('wmf');
  const imageUrl = getContentImageUrl(page.content);
  const description = getContentDescription(page.content);
  return (
    <Layout navigation={navigation} partners={partners}>
      <Meta title={page.title} image={imageUrl} description={description} />
      <div className='relative flex items-center pt-40'>
        <Container className='sm:pt-24 relative z-10 flex flex-col gap-3'>
          <h1 className='before:hidden m-0 font-black text-4xl sm:text-6xl font-primary text-neutral-950'>
            {page.title}
          </h1>
        </Container>
      </div>
      <Container>
        <Content content={page.content} />
        {page.path === 'tickets' && <FanRegister />}
        {draw && draw.tables.length > 0 && (
          <div className='my-12 sm:my-24 flex flex-col gap-4 md:gap-6 lg:gap-10'>
            <h2 className='text-3xl md:text-5xl font-black font-primary text-neutral-950 m-0'>
              {t('draw.live.tables')}
            </h2>
            <HomeGroups draw={draw} />
          </div>
        )}
      </Container>
    </Layout>
  );
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  let paths: { params: { path: string[] }; locale: string }[] = [];

  for (const locale of locales ?? []) {
    const navigation = await getNavigation(getLanguageLocale(locale));
    navigation?.forEach(item => {
      if (item.type === NavigationType.link) {
        return;
      }

      if (item.type === NavigationType.page) {
        paths.push({ params: { path: [item.path] }, locale });
        return;
      }

      for (const itemChild of item.children ?? []) {
        if (!itemChild) {
          continue;
        }

        if (itemChild.type === NavigationType.link) {
          continue;
        }

        paths.push({ params: { path: itemChild.path.split('/') }, locale });
      }
    });
  }

  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const { path } = params as { path: string[] };
  const slug = path.slice(-1).pop();
  if (!slug) {
    return {
      notFound: true,
    };
  }

  let navigation: NavigationItemFragment[];
  let page: PageDetailFragment | null | undefined;
  try {
    const data = await sdk.StaticPagePage({
      union: process.env.NEXT_PUBLIC_UNION,
      locale: getLanguageLocale(locale ?? 'cz'),
      path: slug,
    });
    navigation = data.navigation;
    page = data.page;
  } catch {
    return {
      notFound: true,
    };
  }

  if (!page) {
    return {
      notFound: true,
    };
  }

  let draw: DrawDetailItemFragment | null = null;
  if (page.path === 'schedule') {
    const { draw: d } = await sdk.DrawDetail({
      union: process.env.NEXT_PUBLIC_UNION,
      id: '61bd614c-ba2e-48d7-ac30-1a486d084373',
    });

    draw = d;
  }

  return {
    props: {
      navigation,
      page,
      draw,
      partners: await getPartners(),
    },
    revalidate: 10,
  };
};
